// Lightweight per-key notification sound. Silent no-op if file missing or muted.
// Drop MP3/WAV files at /public/sounds/<key>.mp3 to enable.
import { getSoundEnabled, getSoundVolume } from "@/lib/soundSettings";

const SOUNDS: Record<string, string> = {
  "rfq-received": "/sounds/rfq-received.mp3",
  "quote-received": "/sounds/quote-received.mp3",
  "bid-event": "/sounds/bid-event.mp3",
  "bid-won": "/sounds/bid-won.mp3",
  "alert-ping": "/sounds/alert-ping.mp3",
  "private-load": "/sounds/private-load.mp3",
  "dm-received": "/sounds/dm-received.mp3",
};

const cache = new Map<string, HTMLAudioElement>();

// Browser autoplay policy blocks HTMLAudio.play() until the page has
// received a user gesture. Without an unlock, the *first* DM after page
// load never dings — the exact reproduction the user reported. On the
// first pointerdown/keydown/touchstart anywhere, we silently prime every
// cached Audio element so subsequent .play() calls succeed.
let audioUnlocked = false;
function installAudioUnlock() {
  if (typeof window === "undefined") return;
  if (audioUnlocked) return;
  const unlock = () => {
    if (audioUnlocked) return;
    audioUnlocked = true;
    for (const key of Object.keys(SOUNDS)) get(key);
    for (const a of cache.values()) {
      try {
        const prevVol = a.volume;
        a.volume = 0;
        const p = a.play();
        if (p && typeof p.then === "function") {
          p.then(() => { try { a.pause(); a.currentTime = 0; a.volume = prevVol; } catch { /* ignore */ } })
            .catch(() => { try { a.volume = prevVol; } catch { /* ignore */ } });
        }
      } catch { /* ignore */ }
    }
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("keydown", unlock, true);
    window.removeEventListener("touchstart", unlock, true);
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
  window.addEventListener("touchstart", unlock, true);
}

function get(key: string): HTMLAudioElement | null {
  if (typeof window === "undefined") return null;
  const src = SOUNDS[key];
  if (!src) return null;
  let a = cache.get(key);
  if (!a) {
    a = new Audio(src);
    a.preload = "auto";
    cache.set(key, a);
  }
  a.volume = getSoundVolume();
  return a;
}

if (typeof window !== "undefined") {
  installAudioUnlock();
}

/** Return the shared cached Audio elements for every registered key (SSR-safe). */
export function getAllCachedSounds(): HTMLAudioElement[] {
  if (typeof window === "undefined") return [];
  for (const key of Object.keys(SOUNDS)) get(key);
  return Array.from(cache.values());
}

export const notifySound = {
  play(key: keyof typeof SOUNDS | string) {
    if (!getSoundEnabled()) {
      console.debug("[notify-sound] skipped (disabled)", key);
      return;
    }
    const a = get(key);
    if (!a) {
      console.warn("[notify-sound] no audio element for key", key);
      return;
    }
    console.debug("[notify-sound] play", key, a.src);
    try {
      a.currentTime = 0;
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => console.warn("[notify-sound] play rejected", key, err?.name ?? err));
      }
    } catch (err) {
      console.warn("[notify-sound] play threw", key, err);
    }
  },
};
